import React from 'react';

/**
 * ProjectDemoModal — Editorial documentary case study modal overlay.
 * Displays video demos inside device frames, key architecture highlights,
 * technology badges, and repository/download links.
 */
const ProjectDemoModal = ({ project, onClose, onPlay }) => {
  if (!project) return null;

  const isVideo = project.videoUrl;

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-editorial-card editorial-card technical" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title-group">
            <span className="font-mono modal-type-chip">
              {project.flagship ? 'FLAGSHIP CASE STUDY' : 'PROJECT ARTIFACT'}
            </span>
            <h2 className="font-display modal-title">{project.title}</h2>
            <span className="font-mono modal-tagline">{project.tagline}</span>
          </div>
          <button className="modal-close-btn font-mono" onClick={onClose} aria-label="Close modal">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {isVideo ? (
            <div className="video-preview-wrapper">
              <div className="device-frame">
                <div className="device-notch" />
                <video
                  className="demo-video-player"
                  src={project.videoUrl}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              </div>
            </div>
          ) : null}

          <div className="showcase-content">
            <p className="font-body modal-description">{project.desc || project.description}</p>

            {project.highlights && project.highlights.length > 0 && (
              <div className="highlights-box">
                <h4 className="font-mono highlights-title">KEY ARCHITECTURE & FEATURES</h4>
                <ul className="highlights-list">
                  {project.highlights.map((item, idx) => (
                    <li key={idx} className="font-body">{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Tech Stack Chips */}
          <div className="modal-tech-stack">
            <h4 className="font-mono tech-title">TECHNOLOGY STACK</h4>
            <div className="tech-chips-row">
              {project.tech ? project.tech.map((t) => (
                <span key={t} className="tech-badge font-mono">{t}</span>
              )) : project.technologies?.map((t) => (
                <span key={t} className="tech-badge font-mono">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          {project.playable ? (
            <button
              className="btn-editorial btn-editorial-primary"
              onClick={() => {
                onClose();
                onPlay();
              }}
            >
              Play in Browser
            </button>
          ) : project.repo ? (
            <a
              href={project.repo}
              target="_blank"
              rel="noreferrer"
              className="btn-editorial btn-editorial-primary"
            >
              View Repository
            </a>
          ) : null}
          <button className="btn-editorial btn-editorial-secondary" onClick={onClose}>
            Close Preview
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(4, 6, 10, 0.85);
          backdrop-filter: blur(10px);
          z-index: var(--z-modal, 2000);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-4);
          animation: fadeIn 0.25s ease-out;
        }

        .modal-editorial-card {
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          overflow-y: auto;
          background: var(--bg-surface);
          border: 1px solid var(--border-medium);
          padding: var(--space-6);
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding-bottom: var(--space-4);
          border-bottom: 1px solid var(--border-subtle);
        }

        .modal-title-group {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .modal-type-chip {
          font-size: 0.72rem;
          color: var(--accent-gold);
          letter-spacing: 0.12em;
        }

        .modal-title {
          font-size: 1.5rem;
          color: var(--text-main);
        }

        .modal-tagline {
          font-size: 0.8rem;
          color: var(--accent-blue);
        }

        .modal-close-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--border-medium);
          color: var(--text-muted);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          color: var(--accent-red);
          border-color: var(--accent-red);
        }

        .modal-body {
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
        }

        .video-preview-wrapper {
          display: flex;
          justify-content: center;
        }

        .device-frame {
          position: relative;
          width: 260px;
          background: #000;
          border: 8px solid #1e293b;
          border-radius: 32px;
          overflow: hidden;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
        }

        .device-notch {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 16px;
          background: #1e293b;
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
          z-index: 10;
        }

        .demo-video-player {
          width: 100%;
          height: 440px;
          object-fit: cover;
          display: block;
        }

        .modal-description {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: var(--line-height-body);
        }

        .highlights-box {
          background: rgba(0, 0, 0, 0.25);
          padding: var(--space-4);
          border-radius: 10px;
          border: 1px solid var(--border-subtle);
          margin-top: var(--space-3);
        }

        .highlights-title {
          font-size: 0.72rem;
          color: var(--accent-gold);
          letter-spacing: 0.12em;
          margin-bottom: var(--space-3);
        }

        .highlights-list {
          padding-left: var(--space-4);
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        .highlights-list li {
          color: var(--text-main);
          font-size: 0.875rem;
        }

        .tech-title {
          font-size: 0.72rem;
          color: var(--text-dim);
          letter-spacing: 0.12em;
          margin-bottom: var(--space-2);
        }

        .tech-chips-row {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-2);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-3);
          padding-top: var(--space-4);
          border-top: 1px solid var(--border-subtle);
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ProjectDemoModal;
